import { useState, useEffect } from 'react';

interface LeverageModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentLeverage: number;
  maxLeverage: number;
  onConfirm: (leverage: number) => void;
}

export function LeverageModal({ isOpen, onClose, currentLeverage, maxLeverage, onConfirm }: LeverageModalProps) {
  const [leverage, setLeverage] = useState(currentLeverage);

  // Sync with prop when modal opens
  useEffect(() => {
    if (isOpen) {
      setLeverage(currentLeverage);
    }
  }, [isOpen, currentLeverage]);

  if (!isOpen) return null;

  // Generate preset values based on maxLeverage
  const generatePresets = () => {
    const presets = [1];
    const candidates = [2, 3, 5, 10, 20, 25, 50, 75, 100, 125, 150, 200];
    for (const p of candidates) {
      if (p <= maxLeverage && presets.length < 6) {
        presets.push(p);
      }
    }
    if (!presets.includes(maxLeverage) && presets.length < 7) {
      presets.push(maxLeverage);
    }
    return [...new Set(presets)].sort((a, b) => a - b);
  };

  const presets = generatePresets();

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[#0e0f10] border border-[#2d3748] rounded-lg p-5 w-[360px] max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold text-[15px]">Adjust Leverage</h3>
          <button
            onClick={onClose}
            className="text-[#6c7884] hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Current Value Display */}
        <div className="text-center mb-4">
          <span className="text-[32px] font-bold text-white">{leverage}x</span>
        </div>

        {/* Slider */}
        <div className="mb-4 px-1">
          <input
            type="range"
            min="1"
            max={maxLeverage}
            step="1"
            value={leverage}
            onChange={(e) => setLeverage(Number(e.target.value))}
            className="w-full h-2 bg-[#1f2937] rounded-lg appearance-none cursor-pointer slider-thumb"
            style={{
              background: `linear-gradient(to right, #3fb68b 0%, #3fb68b ${((leverage - 1) / (maxLeverage - 1)) * 100}%, #1f2937 ${((leverage - 1) / (maxLeverage - 1)) * 100}%, #1f2937 100%)`
            }}
          />
          <div className="flex justify-between mt-2 text-[12px] text-[#6c7884]">
            <span>1x</span>
            <span>{maxLeverage}x</span>
          </div>
        </div>

        {/* Quick Presets */}
        <div className="flex flex-wrap gap-2 mb-5">
          {presets.map((preset) => (
            <button
              key={preset}
              onClick={() => setLeverage(preset)}
              className={`flex-1 min-w-[50px] py-2 px-2 rounded-md text-[12px] font-medium transition-all ${
                leverage === preset
                  ? 'bg-[#3fb68b] text-white'
                  : 'bg-[#0b0c0d] text-[#6c7884] border border-[#2d3748] hover:border-[#3d444d] hover:text-white'
              }`}
            >
              {preset}x
            </button>
          ))}
        </div>

        {/* Info */}
        <p className="text-[#6c7884] text-[12px] mb-4 leading-relaxed">
          Higher leverage increases both potential profits and potential losses. Max leverage for this market is {maxLeverage}x.
        </p>

        {/* Confirm Button */}
        <button
          onClick={() => {
            onConfirm(leverage);
            onClose();
          }}
          className="w-full py-2.5 bg-[#3fb68b] hover:bg-[#36a07a] active:bg-[#2d8a69] text-white font-semibold text-[13px] rounded-md transition-all"
        >
          Confirm
        </button>
      </div>
    </div>
  );
}
